import { isFeatureFlagKey } from '~~/layers/base/constants/featureFlag'
import {
    resolveFeatureFlagForRoute,
    type FeatureFlagRoute,
} from '~~/layers/base/utils/resolveFeatureFlagForRoute'

/**
 * Named route middleware: blocks a route whose feature flag is off
 * (`SPEC_feature_flags_TK-12413.md`).
 *
 * Usage on a page:
 * ```ts
 * definePageMeta({ middleware: ['feature-enabled'], featureFlag: 'contacts-page' })
 * ```
 *
 * For a path serving more than one feature, use `featureFlagByParam` instead — see
 * `utils/resolveFeatureFlagForRoute.ts`, which owns that resolution and is unit-tested.
 *
 * Awaits `ensureFlagsLoaded()` so the gated page never renders before the answer is known — the
 * store dedupes, so several gated navigations share one request. A page without a `featureFlag`
 * meta key passes through untouched.
 *
 * Blocked users land on `/` (ADR-006). Home is never gated, so this cannot loop.
 *
 * **This is a UX gate and nothing more.** `requireFeatureEnabled`
 * (server/utils/resolveFeatureFlags.ts, SPEC A6) exists and is tested, but has **no call site
 * anywhere in the repo** — the six contacts write routes were the only callers and TK-13117
 * removed them. So a direct API call reaches its handler regardless of any flag; auth and
 * ownership still apply, the flag does not. Re-adding a guard is one line per route.
 *
 * This comment previously claimed the opposite. If flags ever need to be a real capability gate,
 * change the code and then this line — not the other way round.
 */
export default defineNuxtRouteMiddleware(async (to) => {
    const key = resolveFeatureFlagForRoute(to as unknown as FeatureFlagRoute)

    if (!key) {
        return
    }

    // An unknown key is a coding mistake (typo in page meta). Fail closed rather than silently
    // letting the route through — a gate that quietly stops gating is worse than a visible block.
    if (!isFeatureFlagKey(key)) {
        // Surfaces a page-meta typo that would otherwise present as an unexplained redirect.
        // eslint-disable-next-line no-console
        console.warn(
            `[featureFlags] Page "${String(to.path)}" declares unknown featureFlag "${String(key)}" — blocking. Valid keys are in constants/featureFlag.ts.`
        )
        return navigateTo('/', { replace: true })
    }

    const featureFlagsStore = useFeatureFlagsStore()
    await featureFlagsStore.ensureFlagsLoaded()

    if (featureFlagsStore.isEnabled(key)) {
        return
    }

    return navigateTo('/', { replace: true })
})
